import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Custom from "../../Components/Buttons/Custom";
import Spinner from "../../Components/Loader/Spinner";
import SVG from "../../Components/SVG";
import { API_BASE_URL } from "../../config/api";

const VERIFY_URL = `${API_BASE_URL}payment-payout/directpay/verify/`;
const REFRESH_URL = `${API_BASE_URL}auth/trader/jwt/refresh/`;
const REQUIRED_PARAMS = [
  "status",
  "order_id",
];
const DIRECTPAY_PARAMS = [
  ...REQUIRED_PARAMS,
  "ref_num",
  "transaction_id",
  "card_number",
  "tracking_code",
];

const parseDirectpaySearch = (search) => {
  const searchParams = new URLSearchParams(search);
  const params = Object.fromEntries(searchParams.entries());

  if (DIRECTPAY_PARAMS.every((key) => params[key])) {
    return params;
  }

  const commaSeparatedParams = decodeURIComponent(search.replace(/^\?/, ""))
    .split(",")
    .reduce((nextParams, part) => {
      const [key, ...valueParts] = part.trim().split("=");
      if (DIRECTPAY_PARAMS.includes(key) && valueParts.length > 0) {
        nextParams[key] = valueParts.join("=");
      }
      return nextParams;
    }, {});

  return {
    ...params,
    ...commaSeparatedParams,
  };
};

function PaymentStatusCard({
  svgName,
  header,
  description,
  buttonText,
  onClick,
  isLoading = false,
}) {
  return (
    <div className="w-full h-full flex justify-center items-center">
      <section className="max-w-sm mx-auto shadow-lg bg-zi-bg2-light dark:bg-zi-bg2-dark p-5 rounded-2xl border border-zi-border-dark">
        <div className="flex flex-col items-center p-5">
          <div>
            {isLoading ? (
              <Spinner size={80} color="#0A84FF" stroke={6} />
            ) : (
              <SVG
                name={svgName}
                classname="size-24 stroke-2 stroke-zi-text-light dark:stroke-zi-text-dark"
              />
            )}
          </div>

          <h2 className="mt-4 text-xl font-medium text-center text-zi-text-light dark:text-zi-text-dark">
            {header}
          </h2>

          <p className="mt-2 text-center text-zi-text-light/50 dark:text-zi-text-dark/50">
            {description}
          </p>
        </div>
        {buttonText && (
          <Custom
            onClick={onClick}
            text={buttonText}
            className="px-5 min-w-40 whitespace-nowrap bg-zi-gradient-button-blue text-zi-text-dark hocus:ring-offset-1 hocus:ring-1 hocus:ring-zi-blue-light"
            textShadow={true}
          />
        )}
      </section>
    </div>
  );
}

export default function DirectpayFinalized() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const [viewState, setViewState] = useState("processing");

  const paymentParams = useMemo(() => {
    return parseDirectpaySearch(search);
  }, [search]);

  const verifyBody = useMemo(
    () =>
      DIRECTPAY_PARAMS.reduce((body, key) => {
        body[key] = paymentParams[key] ?? "";
        return body;
      }, {}),
    [paymentParams],
  );

  const hasMissingRequiredParams = useMemo(
    () => REQUIRED_PARAMS.some((key) => !paymentParams[key]),
    [paymentParams],
  );

  const logout = useCallback(() => {
    localStorage.removeItem("access");
    localStorage.removeItem("authCredentials");
    localStorage.removeItem("refresh");
    localStorage.removeItem("role");
    localStorage.removeItem("email");
    navigate("/signin");
  }, [navigate]);

  const refreshAccessToken = useCallback(async () => {
    const refreshResponse = await fetch(REFRESH_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        refresh: localStorage.getItem("refresh"),
      }),
    });

    if (!refreshResponse.ok) {
      logout();
      return null;
    }

    const refreshData = await refreshResponse.json();
    localStorage.setItem("access", refreshData.access);
    return refreshData.access;
  }, [logout]);

  const fetchWithAuthRetry = useCallback(
    async (url, options = {}) => {
      const getRequestOptions = () => ({
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...(options.headers ?? {}),
          Authorization: `Bearer ${localStorage.getItem("access")}`,
        },
      });

      let response = await fetch(url, getRequestOptions());

      if (response.status !== 401) {
        return response;
      }

      const nextAccessToken = await refreshAccessToken();
      if (!nextAccessToken) {
        return null;
      }

      return fetch(url, getRequestOptions());
    },
    [refreshAccessToken],
  );

  const verifyPayment = useCallback(async () => {
    if (hasMissingRequiredParams) {
      setViewState("invalid");
      return;
    }

    setViewState("processing");

    try {
      const response = await fetchWithAuthRetry(VERIFY_URL, {
        method: "POST",
        body: JSON.stringify(verifyBody),
      });

      if (!response) {
        return;
      }

      if (response.status === 404) {
        setViewState("notFound");
        return;
      }

      if (response.status >= 500) {
        setViewState("retryable");
        return;
      }

      if (!response.ok) {
        setViewState("failed");
        return;
      }

      const data = await response.json().catch(() => null);
      const status = String(data?.status ?? data?.data?.status ?? "")
        .trim()
        .toLowerCase();

      if (status === "success") {
        setViewState("success");
        return;
      }

      if (status === "failed") {
        setViewState("failed");
        return;
      }

      setViewState("retryable");
    } catch (error) {
      console.error("Error verifying Directpay payment:", error);
      setViewState("retryable");
    }
  }, [fetchWithAuthRetry, hasMissingRequiredParams, verifyBody]);

  useEffect(() => {
    verifyPayment();
  }, [verifyPayment]);

  const variants = {
    processing: {
      svgName: "check",
      header: "پرداخت شما در حال پردازش می باشد",
      description: "لطفا تا پایان بررسی پرداخت منتظر بمانید.",
      isLoading: true,
    },
    success: {
      svgName: "check",
      header: "پرداخت موفق!",
      description: "خرید چالش با موفقیت انجام شد.",
      buttonText: "انتقال به صفحه حساب‌ها و چالش‌ها",
      onClick: () => navigate("/challenge"),
    },
    failed: {
      svgName: "close",
      header: "پرداخت ناموفق!",
      description: "خرید چالش موفق نبود، لطفا مجددا تلاش کنید.",
      buttonText: "انتقال به صفحه میز کار",
      onClick: () => navigate("/dashboard"),
    },
    invalid: {
      svgName: "close",
      header: "پرداخت نامعتبر!",
      description: "اطلاعات پرداخت کامل نیست، لطفا مجددا تلاش کنید.",
      buttonText: "انتقال به صفحه میز کار",
      onClick: () => navigate("/dashboard"),
    },
    notFound: {
      svgName: "close",
      header: "پرداخت مورد نظر یافت نشد",
      description: "اطلاعات این پرداخت در سیستم پیدا نشد.",
      buttonText: "انتقال به صفحه میز کار",
      onClick: () => navigate("/dashboard"),
    },
    retryable: {
      svgName: "close",
      header: "بررسی پرداخت با خطا مواجه شد",
      description: "ارتباط با سرور برقرار نشد. لطفا دوباره تلاش کنید.",
      buttonText: "تلاش مجدد",
      onClick: verifyPayment,
    },
  };

  return <PaymentStatusCard {...variants[viewState]} />;
}
